import {Type} from 'io-ts';
import {EffectCallback, useRef} from 'react';

import {isDevelopment} from '../../libs/shared/helpers/src/environment/envHelpers';
import {useConstant} from '../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../libs/shared/helpers/src/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {delayAsync} from '../../src/helpers/asyncHelpers';
import {useAppSelector} from '../../src/helpers/reduxHelpers';
import {ApiType, ModelType} from '../../src/models/api/apiTypes';
import {deleteApiAsync, fetchApiAsync, postApiAsync, putApiAsync} from '../../src/services/http/http';
import {getHasReact18StrictMode} from '../../src/store/featureFlags/featureFlagsSelectors';

/*
 * Use Timeout.
 */

interface UseApiPublicOptions<RTYPE extends Type<any>> {
  apiType: ApiType<RTYPE>;
  debounceDelay?: number;
  onStateChange(state: UseApiState<ModelType<RTYPE>>): void;
}

interface UseApiOptions<RTYPE extends Type<any>> extends UseApiPublicOptions<RTYPE> {
  method: 'get' | 'post' | 'put' | 'delete';
}

export type UseApiState<RESULT> = (
  | {type: 'loaded'; result: RESULT; error?: undefined}
  | {type: 'loading'; result?: undefined; error?: undefined}
  | {type: 'failed'; error: unknown; result?: undefined}
) & {asyncStatus: AsyncStatusesEnum};

/** @deprecated: Use useDebounce and fetchApiAsync instead. */
export function useFetchApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'get', ...options});
}

/** @deprecated: Use useDebounce and postApiAsync instead. */
export function usePostApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'post', ...options});
}

/** @deprecated: Use useDebounce and putApiAsync instead. */
export function usePutApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'put', ...options});
}

/** @deprecated: Use useDebounce and deleteApiAsync instead. */
export function useDeleteApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'delete', ...options});
}

/* Makes it easier to perform http calls within a React component, with support for debouncing and cancelling */
function useApi<RTYPE extends Type<any>>(options: UseApiOptions<RTYPE>) {
  const {apiType, method, debounceDelay, onStateChange} = options;

  const lastCancelTokenSource = useRef<CancelTokenSource>();

  function cancel() {
    if (lastCancelTokenSource.current) {
      lastCancelTokenSource.current.cancel();
    }
  }

  useInitialEffect(() => cancel);

  function apiCall(path: string, body?: unknown, contentType?: string) {
    cancel();
    onStateChange({type: 'loading', asyncStatus: AsyncStatusesEnum.LOADING});

    const cancelTokenSource = buildCancelTokenSource();
    lastCancelTokenSource.current = cancelTokenSource;

    const cancelToken = cancelTokenSource.token;

    const httpOptions = {
      cancelToken,
      headers: (contentType && {'content-type': contentType}) || undefined,
    };

    const delayPromise = debounceDelay ? delayAsync(debounceDelay, cancelToken) : Promise.resolve();

    return delayPromise
      .then(() => {
        switch (method) {
          case 'post':
            return postApiAsync(apiType, path, body, httpOptions);

          case 'put':
            return putApiAsync(apiType, path, body, httpOptions);

          case 'delete':
            return deleteApiAsync(apiType, path, body, httpOptions);

          default:
            return fetchApiAsync(apiType, path, httpOptions);
        }
      })
      .then((result) => {
        onStateChange({type: 'loaded', result, asyncStatus: AsyncStatusesEnum.LOADED});
        return result;
      })
      .catch((error) => {
        if (error instanceof CancelError) {
          return undefined;
        }

        onStateChange({type: 'failed', error, asyncStatus: AsyncStatusesEnum.FAILED});
        return undefined;
      });
  }

  return apiCall;
}

export function useUnstrictInitialEffect(effect: EffectCallback) {
  const hasStrictMode = useAppSelector(getHasReact18StrictMode);
  const isStrictModeEnabled = useConstant(() => hasStrictMode && isDevelopment());

  const cleanupFunctionRef = useRef<ReturnType<EffectCallback>>();
  const effectCountRef = useRef(0);
  const cleanupCountRef = useRef(0);
  useInitialEffect(() => {
    if (!isStrictModeEnabled) {
      return effect();
    }

    if (effectCountRef.current === 0) {
      // If this is the first time we're running the effect, run it
      // and save the cleanup function.
      cleanupFunctionRef.current = effect();
    }
    // Update the effect run count.
    effectCountRef.current += 1;

    return () => {
      if (cleanupCountRef.current === 1) {
        // If this is the second time we're cleaning up the effect,
        // actually do the cleanup.
        cleanupFunctionRef.current?.();
      }
      // Update the cleanup run count.
      cleanupCountRef.current += 1;
    };
  });
}
